// ========================================
// SYSTÈME DE VALIDATION DES GRAPHIQUES
// ========================================

import type { FlexibleChartConfig } from '../types/customCharts'

// Interface de validation
export interface ValidationRule {
  id: string
  condition: (config: FlexibleChartConfig) => boolean
  message: string
  severity: 'error' | 'warning' | 'info'
  mobileMessage?: string // Message adapté mobile (plus court)
}

// Résultat de validation
export interface ValidationResult {
  valid: boolean
  errors: ValidationRule[]
  warnings: ValidationRule[]
  infos: ValidationRule[]
}

// Règles de validation
export const VALIDATION_RULES: ValidationRule[] = [
  // ========================================
  // RÈGLES D'ERREUR (bloquantes)
  // ========================================
  {
    id: 'pie-single-series',
    condition: (config) => config.chartType === 'pie' && config.dataSources.length > 1,
    message: 'Un graphique en secteurs ne peut avoir qu\'une seule série de données',
    mobileMessage: 'Secteurs : 1 seule série max',
    severity: 'error'
  },
  {
    id: 'doughnut-single-series',
    condition: (config) => config.chartType === 'doughnut' && config.dataSources.length > 1,
    message: 'Un graphique en anneau ne peut avoir qu\'une seule série de données',
    mobileMessage: 'Anneau : 1 seule série max',
    severity: 'error'
  },
  {
    id: 'dual-axis-pie-error',
    condition: (config) => !!config.yAxis2 && ['pie', 'doughnut'].includes(config.chartType),
    message: 'Les axes doubles ne sont pas compatibles avec les graphiques en secteurs/anneau',
    mobileMessage: 'Secteurs : pas d\'axes doubles',
    severity: 'error'
  },
  {
    id: 'no-data-sources',
    condition: (config) => config.dataSources.length === 0,
    message: 'Au moins une source de données est requise',
    mobileMessage: 'Aucune source de données',
    severity: 'error'
  },
  {
    id: 'empty-datasource-name',
    condition: (config) => config.dataSources.some(ds => !ds.name.trim()),
    message: 'Toutes les sources de données doivent avoir un nom',
    mobileMessage: 'Noms manquants',
    severity: 'error'
  },
  {
    id: 'empty-period',
    condition: (config) => !config.period.start || !config.period.end,
    message: 'Une période de dates doit être sélectionnée',
    mobileMessage: 'Période manquante',
    severity: 'error'
  },
  {
    id: 'invalid-period-range',
    condition: (config) => {
      if (!config.period.start || !config.period.end) return false
      return new Date(config.period.start) >= new Date(config.period.end)
    },
    message: 'La date de fin doit être postérieure à la date de début',
    mobileMessage: 'Période invalide',
    severity: 'error'
  },
  {
    id: 'invalid-comparison-period',
    condition: (config) => {
      if (!config.showComparison || !config.comparisonPeriod) return false
      const { start, end } = config.comparisonPeriod
      if (!start || !end) return true // Erreur si comparaison activée mais période manquante
      return new Date(start) >= new Date(end)
    },
    message: 'La période de comparaison est invalide',
    mobileMessage: 'Comparaison invalide',
    severity: 'error'
  },
  
  // ========================================
  // RÈGLES D'AVERTISSEMENT (non-bloquantes)
  // ========================================
  {
    id: 'performance-warning',
    condition: (config) => config.dataSources.some(ds => !ds.limit || ds.limit > 1000),
    message: 'Performance : considérez limiter les données à 1000 éléments pour améliorer la vitesse d\'affichage',
    mobileMessage: 'Trop de données = lenteur',
    severity: 'warning'
  },
  {
    id: 'too-many-series',
    condition: (config) => config.dataSources.length > 5,
    message: 'Trop de séries peuvent rendre le graphique difficile à lire',
    mobileMessage: 'Beaucoup de séries',
    severity: 'warning'
  },
  {
    id: 'median-pie-warning',
    condition: (config) => ['pie', 'doughnut'].includes(config.chartType) && 
                          config.dataSources.some(ds => ds.aggregation === 'median'),
    message: 'La médiane n\'est pas recommandée pour les graphiques en secteurs',
    mobileMessage: 'Médiane + secteurs déconseillé',
    severity: 'warning'
  },
  {
    id: 'area-mobile-warning',
    condition: (config) => config.chartType === 'area' && config.mobileConfig?.compactMode,
    message: 'Les graphiques en aires peuvent être difficiles à lire sur mobile',
    mobileMessage: 'Aires difficiles sur mobile',
    severity: 'warning'
  },
  {
    id: 'long-period-hourly',
    condition: (config) => {
      if (config.xAxis.timeAggregation !== 'hour') return false
      if (!config.period.start || !config.period.end) return false
      const diffDays = (new Date(config.period.end).getTime() - new Date(config.period.start).getTime()) / (1000 * 60 * 60 * 24)
      return diffDays > 7
    },
    message: 'L\'agrégation horaire sur une longue période peut générer beaucoup de points',
    mobileMessage: 'Période longue + heure = beaucoup de points',
    severity: 'warning'
  },
  
  // ========================================
  // RÈGLES D'INFORMATION (suggestions)
  // ========================================
  {
    id: 'smart-aggregation-suggestion',
    condition: (config) => {
      if (!config.period.start || !config.period.end) return false
      const diffDays = (new Date(config.period.end).getTime() - new Date(config.period.start).getTime()) / (1000 * 60 * 60 * 24)
      const currentAgg = config.xAxis.timeAggregation
      
      if (diffDays <= 3 && currentAgg !== 'hour') return true
      if (diffDays > 3 && diffDays <= 90 && currentAgg !== 'day') return true
      if (diffDays > 365 && currentAgg !== 'month') return true
      
      return false
    },
    message: 'Une agrégation temporelle différente pourrait être plus adaptée à cette période',
    mobileMessage: 'Agrégation suggérée disponible',
    severity: 'info'
  },
  {
    id: 'dual-axis-suggestion',
    condition: (config) => {
      if (!config.yAxis2 && config.dataSources.length >= 2) {
        // Suggérer un axe double si les données ont des ordres de grandeur différents
        const hasMoneyField = config.dataSources.some(ds => ds.field.toLowerCase().includes('amount') || ds.field.toLowerCase().includes('total'))
        const hasCountField = config.dataSources.some(ds => ds.aggregation === 'count')
        return hasMoneyField && hasCountField
      }
      return false
    },
    message: 'Un axe secondaire pourrait améliorer la lisibilité avec des données d\'échelles différentes',
    mobileMessage: 'Axe secondaire suggéré',
    severity: 'info'
  }
]

// ========================================
// FONCTIONS DE VALIDATION
// ========================================

// Fonction principale de validation
export function validateChart(config: FlexibleChartConfig): ValidationResult {
  const errors: ValidationRule[] = []
  const warnings: ValidationRule[] = []
  const infos: ValidationRule[] = []

  for (const rule of VALIDATION_RULES) {
    if (rule.condition(config)) {
      switch (rule.severity) {
        case 'error':
          errors.push(rule)
          break
        case 'warning':
          warnings.push(rule)
          break
        case 'info':
          infos.push(rule)
          break
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    infos
  }
}

// Validation rapide (uniquement les erreurs)
export function isChartValid(config: FlexibleChartConfig): boolean {
  return validateChart(config).valid
}

// Messages de validation adaptés mobile
export function getValidationMessages(result: ValidationResult, isMobile: boolean = false): string[] {
  const allMessages: string[] = []
  
  const getMessage = (rule: ValidationRule) => 
    isMobile && rule.mobileMessage ? rule.mobileMessage : rule.message

  // Ajouter les erreurs en premier
  result.errors.forEach(error => {
    allMessages.push(`❌ ${getMessage(error)}`)
  })

  // Puis les avertissements
  result.warnings.forEach(warning => {
    allMessages.push(`⚠️ ${getMessage(warning)}`)
  })

  // Enfin les informations (seulement si pas trop de messages)
  if (allMessages.length <= 2) {
    result.infos.forEach(info => {
      allMessages.push(`💡 ${getMessage(info)}`)
    })
  }

  return allMessages
}

// Validation en temps réel avec debounce (pour performance)
export class ChartValidator {
  private debounceTimeout: number | null = null
  private lastConfig: FlexibleChartConfig | null = null
  private lastResult: ValidationResult | null = null

  validate(config: FlexibleChartConfig, callback: (result: ValidationResult) => void, delay: number = 300) {
    // Cache simple pour éviter les re-validations inutiles
    if (this.lastConfig && JSON.stringify(this.lastConfig) === JSON.stringify(config)) {
      if (this.lastResult) {
        callback(this.lastResult)
        return
      }
    }

    // Debounce pour éviter trop de validations
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }

    this.debounceTimeout = window.setTimeout(() => {
      const result = validateChart(config)
      this.lastConfig = { ...config }
      this.lastResult = result
      callback(result)
      this.debounceTimeout = null
    }, delay)
  }

  // Validation immédiate (sans debounce)
  validateImmediate(config: FlexibleChartConfig): ValidationResult {
    const result = validateChart(config)
    this.lastConfig = { ...config }
    this.lastResult = result
    return result
  }

  // Nettoyer les timeouts
  cleanup() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = null
    }
  }
}

// Instance globale du validateur
export const chartValidator = new ChartValidator()

// ========================================
// VALIDATION SPÉCIFIQUE MOBILE
// ========================================

// Validation adaptée mobile (plus permissive sur certains points)
export function validateChartForMobile(config: FlexibleChartConfig): ValidationResult {
  const result = validateChart(config)
  
  // Sur mobile, on peut être plus tolérant sur le nombre de séries
  result.warnings = result.warnings.filter(w => w.id !== 'too-many-series' || config.dataSources.length > 8)
  
  // Ajouter des validations spécifiques mobile si nécessaire
  if (config.mobileConfig.compactMode && config.dataSources.length > 3) {
    result.warnings.push({
      id: 'mobile-compact-series',
      condition: () => true,
      message: 'En mode compact, limitez le nombre de séries pour une meilleure lisibilité',
      mobileMessage: 'Mode compact : moins de séries',
      severity: 'warning'
    })
  }

  return result
}